// colresize.js — user-adjustable column widths for every <table class="tbl">.
//
// There is no shared table component in this app; ~15 tables across the screens
// all render plain `<table className="tbl">`. Rather than rewrite each one, this
// module attaches ONE set of delegated listeners to the document:
//
//   • Drag the right edge of any header cell to resize that column.
//   • Widths are remembered per-table (keyed by its header signature) in
//     localStorage and re-applied — including to tables React mounts later.
//   • Double-click an edge to reset that table's columns to their defaults.
//
// Everything is computed from getBoundingClientRect at drag time, so it's robust
// to React re-rendering the table underneath us. The cursor affordance is pure
// CSS (a ::after handle on each th — see app.css), so this file is logic only.

const STORE_KEY = 'vendoros:colw';
const EDGE  = 6;    // px from a header cell's right edge that starts a resize
const MIN_W = 48;   // smallest a column can be dragged to

function loadStore() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {}; }
  catch (_) { return {}; }
}
function saveStore(s) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch (_) { /* quota / private mode */ }
}

function headerCells(table) {
  const headRow = table.tHead && table.tHead.rows[0];
  return headRow ? Array.from(headRow.cells) : [];
}
function colEls(table) {
  const cg = table.querySelector('colgroup');
  return cg ? Array.from(cg.querySelectorAll('col')) : [];
}
// Stable identity for a table: its header texts + column count. Two tables with
// the same headers intentionally share remembered widths; the count guards
// against applying a stale layout after columns change.
function tableKey(table) {
  const cells = headerCells(table);
  if (!cells.length) return null;
  const sig = cells.map(c => (c.textContent || '').replace(/\s+/g, ' ').trim()).join('|');
  return 'cr:' + sig + '#' + cells.length;
}

function setWidth(table, idx, px) {
  const w = Math.round(px) + 'px';
  const cols = colEls(table);
  if (cols[idx]) cols[idx].style.width = w;   // authoritative under table-layout:fixed
  const cells = headerCells(table);
  if (cells[idx]) cells[idx].style.width = w; // covers tables with no <colgroup>
}

// Pin every column to its current rendered width and switch to fixed layout so
// the drag is crisp and nothing else reflows. Returns the measured widths.
function freezeAll(table) {
  const widths = headerCells(table).map(c => c.getBoundingClientRect().width);
  table.style.tableLayout = 'fixed';
  widths.forEach((w, i) => setWidth(table, i, w));
  return widths;
}

function applySaved(table) {
  const key = tableKey(table);
  if (!key) return;
  const widths = loadStore()[key];
  if (!Array.isArray(widths) || !widths.length) return;
  if (widths.length !== headerCells(table).length) return;   // layout changed — ignore
  table.style.tableLayout = 'fixed';
  widths.forEach((w, i) => { if (w) setWidth(table, i, w); });
}

function resetTable(table) {
  const key = tableKey(table);
  if (key) { const store = loadStore(); delete store[key]; saveStore(store); }
  table.style.tableLayout = '';
  colEls(table).forEach(c => { c.style.width = ''; });
  headerCells(table).forEach(c => { c.style.width = ''; });
}

export function installColumnResize() {
  // Apply saved widths to tables already in the DOM…
  document.querySelectorAll('table.tbl').forEach(t => { applySaved(t); t.dataset.crInit = '1'; });

  // …and to any React mounts later.
  const mo = new MutationObserver(muts => {
    for (const m of muts) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        const tables = node.matches && node.matches('table.tbl')
          ? [node]
          : (node.querySelectorAll ? Array.from(node.querySelectorAll('table.tbl')) : []);
        tables.forEach(t => { if (!t.dataset.crInit) { applySaved(t); t.dataset.crInit = '1'; } });
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  // Is the pointer over the right-edge resize zone of a header cell?
  function edgeHit(e) {
    const th = e.target.closest && e.target.closest('table.tbl thead th');
    if (!th) return null;
    const table = th.closest('table.tbl');
    const idx = headerCells(table).indexOf(th);
    if (idx < 0) return null;
    const rect = th.getBoundingClientRect();
    if (rect.right - e.clientX <= EDGE && e.clientX - rect.left > EDGE) return { table, idx };
    return null;
  }

  let drag = null;   // { table, idx, startX, startW, moved }

  function onMove(e) {
    if (!drag) return;
    if (!drag.moved) { drag.moved = true; drag.startW = freezeAll(drag.table)[drag.idx]; }
    setWidth(drag.table, drag.idx, Math.max(MIN_W, drag.startW + (e.clientX - drag.startX)));
  }
  function onUp() {
    if (!drag) return;
    if (drag.moved) {
      const key = tableKey(drag.table);
      if (key) {
        const store = loadStore();
        store[key] = headerCells(drag.table).map(c => c.getBoundingClientRect().width);
        saveStore(store);
      }
      // The browser fires a `click` on the header after a drag ends. Swallow
      // exactly that click (capture phase) so a resize doesn't also trigger the
      // header's sort handler. Bounded to the current tick so it never eats a
      // later, legitimate click.
      const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
      window.addEventListener('click', swallow, true);
      setTimeout(() => window.removeEventListener('click', swallow, true), 0);
    }
    document.body.classList.remove('cr-dragging');
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    drag = null;
  }
  function onDown(e) {
    if (e.button !== 0) return;
    const hit = edgeHit(e);
    if (!hit) return;
    e.preventDefault();   // no text-selection while dragging
    drag = { table: hit.table, idx: hit.idx, startX: e.clientX, startW: 0, moved: false };
    document.body.classList.add('cr-dragging');
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }
  function onDblClick(e) {
    const hit = edgeHit(e);
    if (!hit) return;
    e.preventDefault();
    resetTable(hit.table);
  }

  document.addEventListener('mousedown', onDown, true);
  document.addEventListener('dblclick', onDblClick, true);

  return function cleanup() {
    mo.disconnect();
    document.removeEventListener('mousedown', onDown, true);
    document.removeEventListener('dblclick', onDblClick, true);
  };
}
